import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface SupportTicket {
  id?: number;
  ticket_number: string;
  ticket_category: string;
  name: string;
  job_title?: string;
  phone?: string;
  manager?: string;
  date_time: string;
  id_number: string;
  department: string;
  technician: string;
  problem_description: string;
  assigned_to?: string;
  response_date?: string;
  response_time?: string;
  total_hours_worked?: number;
  initial_action?: string;
  follow_up_action?: string;
  importance_mission_critical?: boolean;
  importance_slowing_user_down?: boolean;
  importance_to_be_scheduled?: boolean;
  conclusion_resolved?: boolean;
  conclusion_pending?: boolean;
  conclusion_unresolved?: boolean;
  final_notes?: string;
  status?: string;
  priority?: string;
  created_by?: number;
  assigned_user_id?: number;
  org_id?: number;
  created_at?: string;
  updated_at?: string;
  resolved_at?: string;
  closed_at?: string;
  created_by_name?: string;
  assigned_user_name?: string;
  comment_count?: number;
  attachment_count?: number;
  comments?: TicketComment[];
  attachments?: TicketAttachment[];
  history?: TicketHistory[];
}

export interface TicketComment {
  id?: number;
  ticket_id: number;
  comment: string;
  comment_type: 'internal' | 'public' | 'system';
  created_by?: number;
  created_at?: string;
  updated_at?: string;
  created_by_name?: string;
}

export interface TicketAttachment {
  id?: number;
  ticket_id: number;
  file_name: string;
  file_path: string;
  file_size?: number;
  file_type?: string;
  uploaded_by?: number;
  uploaded_at?: string;
  uploaded_by_name?: string;
}

export interface TicketHistory {
  id?: number;
  ticket_id: number;
  action: string;
  field_name?: string;
  old_value?: string;
  new_value?: string;
  changed_by?: number;
  changed_at?: string;
  changed_by_name?: string;
}

export interface TicketCategory {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
  created_at: string;
}

export interface TicketPriority {
  id: number;
  name: string;
  color: string;
  description?: string;
  sla_hours?: number;
  is_active: boolean;
  created_at: string;
}

export interface TicketStatus {
  id: number;
  name: string;
  color: string;
  description?: string;
  is_active: boolean;
  created_at: string;
}

export interface TicketFilters {
  page?: number;
  limit?: number;
  status?: string;
  priority?: string;
  category?: string;
  assigned_to?: number;
  created_by?: number;
  search?: string;
  start_date?: string;
  end_date?: string;
  sort_by?: string;
  sort_order?: 'ASC' | 'DESC';
}

export interface TicketStats {
  total_tickets: number;
  open_tickets: number;
  in_progress_tickets: number;
  pending_tickets: number;
  resolved_tickets: number;
  closed_tickets: number;
  critical_tickets: number;
  high_priority_tickets: number;
  medium_priority_tickets: number;
  low_priority_tickets: number;
  avg_resolution_hours?: number;
  tickets_last_7_days: number;
  tickets_last_30_days: number;
  category_distribution: Array<{ ticket_category: string; count: number }>;
  department_distribution: Array<{ department: string; count: number }>;
}

export interface PaginatedResponse<T> {
  tickets: T[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_records: number;
    limit: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

export interface ApiResponse<T> {
  status: string;
  data: T;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class SupportTicketService {
  private apiUrl = `${environment.BASE_URL}/support-tickets`;

  constructor(private http: HttpClient) { }

  /**
   * Get all support tickets with filtering and pagination
   */
  getTickets(filters: TicketFilters = {}): Observable<ApiResponse<PaginatedResponse<SupportTicket>>> {
    let params = new HttpParams();
    
    Object.keys(filters).forEach(key => {
      const value = filters[key as keyof TicketFilters];
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, value.toString());
      }
    });

    return this.http.get<ApiResponse<PaginatedResponse<SupportTicket>>>(this.apiUrl, { params });
  }

  /**
   * Get a single support ticket by ID
   */
  getTicket(id: number): Observable<ApiResponse<SupportTicket>> {
    return this.http.get<ApiResponse<SupportTicket>>(`${this.apiUrl}/${id}`);
  }

  /**
   * Create a new support ticket
   */
  createTicket(ticket: SupportTicket): Observable<ApiResponse<SupportTicket>> {
    return this.http.post<ApiResponse<SupportTicket>>(this.apiUrl, ticket);
  }

  /**
   * Update a support ticket
   */
  updateTicket(id: number, ticket: Partial<SupportTicket>): Observable<ApiResponse<SupportTicket>> {
    return this.http.put<ApiResponse<SupportTicket>>(`${this.apiUrl}/${id}`, ticket);
  }

  /**
   * Delete a support ticket (soft delete)
   */
  deleteTicket(id: number): Observable<ApiResponse<any>> {
    return this.http.delete<ApiResponse<any>>(`${this.apiUrl}/${id}`);
  }

  /**
   * Get support ticket statistics
   */
  getTicketStats(startDate?: string, endDate?: string): Observable<ApiResponse<TicketStats>> {
    let params = new HttpParams();
    if (startDate) params = params.set('start_date', startDate);
    if (endDate) params = params.set('end_date', endDate);
    
    return this.http.get<ApiResponse<TicketStats>>(`${this.apiUrl}/stats`, { params });
  }

  /**
   * Add a comment to a support ticket
   */
  addComment(ticketId: number, comment: string, commentType: 'internal' | 'public' | 'system' = 'internal'): Observable<ApiResponse<TicketComment>> {
    return this.http.post<ApiResponse<TicketComment>>(`${this.apiUrl}/${ticketId}/comments`, {
      comment,
      comment_type: commentType
    });
  }

  /**
   * Get all ticket categories
   */
  getCategories(): Observable<ApiResponse<TicketCategory[]>> {
    return this.http.get<ApiResponse<TicketCategory[]>>(`${this.apiUrl}/categories`);
  }

  /**
   * Get all ticket priorities
   */
  getPriorities(): Observable<ApiResponse<TicketPriority[]>> {
    return this.http.get<ApiResponse<TicketPriority[]>>(`${this.apiUrl}/priorities`);
  }

  /**
   * Get all ticket statuses
   */
  getStatuses(): Observable<ApiResponse<TicketStatus[]>> {
    return this.http.get<ApiResponse<TicketStatus[]>>(`${this.apiUrl}/statuses`);
  }

  /**
   * Utility methods for UI
   */
  getStatusColor(status: string): string {
    const statusColors: { [key: string]: string } = {
      'open': '#007bff',
      'in_progress': '#ffc107',
      'pending': '#fd7e14',
      'resolved': '#28a745',
      'closed': '#6c757d'
    };
    return statusColors[status] || '#666666';
  }

  getPriorityColor(priority: string): string {
    const priorityColors: { [key: string]: string } = {
      'low': '#28a745',
      'medium': '#ffc107',
      'high': '#fd7e14',
      'critical': '#dc3545'
    };
    return priorityColors[priority] || '#666666';
  }

  getStatusText(status: string): string {
    const statusTexts: { [key: string]: string } = {
      'open': 'Open',
      'in_progress': 'In Progress',
      'pending': 'Pending',
      'resolved': 'Resolved',
      'closed': 'Closed'
    };
    return statusTexts[status] || status;
  }

  getPriorityText(priority: string): string {
    const priorityTexts: { [key: string]: string } = {
      'low': 'Low',
      'medium': 'Medium',
      'high': 'High',
      'critical': 'Critical'
    };
    return priorityTexts[priority] || priority;
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatDateTime(dateString: string): string {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getTimeAgo(dateString: string): string {
    if (!dateString) return '';
    
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return `${Math.floor(diffInSeconds / 2592000)}mo ago`;
  }

  /**
   * Generate a new ticket form with default values
   */
  createNewTicketForm(): SupportTicket {
    return {
      ticket_number: '',
      ticket_category: '',
      name: '',
      job_title: '',
      phone: '',
      manager: '',
      date_time: new Date().toISOString().slice(0, 16), // Current date and time
      id_number: '',
      department: '',
      technician: '',
      problem_description: '',
      assigned_to: '',
      response_date: '',
      response_time: '',
      total_hours_worked: 0,
      initial_action: '',
      follow_up_action: '',
      importance_mission_critical: false,
      importance_slowing_user_down: false,
      importance_to_be_scheduled: false,
      conclusion_resolved: false,
      conclusion_pending: false,
      conclusion_unresolved: false,
      final_notes: '',
      status: 'open',
      priority: 'medium'
    };
  }
}
